import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as mesService from '@/services/mesService';
import type {
  IManufacturingOrder,
  IWorkOrder,
  IDefect,
  IQualityCheckpoint,
  IMaterialConsumption,
  ICreateMORequest,
  IUpdateMORequest,
  IUpdateWORequest,
  IAssignOperatorRequest,
  ICreateDefectRequest,
  IUpdateDefectRequest,
  ICreateCheckpointRequest,
  IRecordMaterialRequest,
  IMOFilter,
  MOStatus,
} from '@/types/mes';

// ============================================================================
// Query Keys
// ============================================================================

export const MES_QUERY_KEYS = {
  all: ['mes'] as const,
  mos: (filter?: IMOFilter) => [...MES_QUERY_KEYS.all, 'mos', filter] as const,
  mo: (id: string) => [...MES_QUERY_KEYS.all, 'mo', id] as const,
  wos: (moId: string) => [...MES_QUERY_KEYS.all, 'wos', moId] as const,
  wo: (id: string) => [...MES_QUERY_KEYS.all, 'wo', id] as const,
  defects: (woId: string) => [...MES_QUERY_KEYS.all, 'defects', woId] as const,
  checkpoints: (woId: string) => [...MES_QUERY_KEYS.all, 'checkpoints', woId] as const,
  materials: (woId: string) => [...MES_QUERY_KEYS.all, 'materials', woId] as const,
};

// ============================================================================
// Manufacturing Order Hooks
// ============================================================================

export const useManufacturingOrders = (filter?: IMOFilter) => {
  return useQuery<IManufacturingOrder[], Error>(
    MES_QUERY_KEYS.mos(filter),
    () => mesService.listManufacturingOrders(filter),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useManufacturingOrder = (moId: string) => {
  return useQuery<IManufacturingOrder, Error>(
    MES_QUERY_KEYS.mo(moId),
    () => mesService.getManufacturingOrder(moId),
    {
      enabled: !!moId,
      staleTime: 30000,
    }
  );
};

export const useCreateManufacturingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ICreateMORequest) => mesService.createManufacturingOrder(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.mos());
      },
    }
  );
};

export const useUpdateManufacturingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ moId, data }: { moId: string; data: IUpdateMORequest }) =>
      mesService.updateManufacturingOrder(moId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.mos());
        queryClient.invalidateQueries(MES_QUERY_KEYS.mo(variables.moId));
      },
    }
  );
};

export const useUpdateMOStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ moId, status }: { moId: string; status: MOStatus }) =>
      mesService.updateMOStatus(moId, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.mos());
        queryClient.invalidateQueries(MES_QUERY_KEYS.mo(variables.moId));
      },
    }
  );
};

export const useReleaseMO = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (moId: string) => mesService.releaseMO(moId),
    {
      onSuccess: (_, moId) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.mos());
        queryClient.invalidateQueries(MES_QUERY_KEYS.mo(moId));
        queryClient.invalidateQueries(MES_QUERY_KEYS.wos(moId));
      },
    }
  );
};

// ============================================================================
// Work Order Hooks
// ============================================================================

export const useWorkOrders = (moId: string) => {
  return useQuery<IWorkOrder[], Error>(
    MES_QUERY_KEYS.wos(moId),
    () => mesService.listWorkOrders(moId),
    {
      enabled: !!moId,
      staleTime: 30000,
      cacheTime: 300000,
      refetchInterval: 60000, // Refresh every minute for active production
    }
  );
};

export const useWorkOrder = (woId: string) => {
  return useQuery<IWorkOrder, Error>(
    MES_QUERY_KEYS.wo(woId),
    () => mesService.getWorkOrder(woId),
    {
      enabled: !!woId,
      staleTime: 30000,
    }
  );
};

export const useUpdateWorkOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, data }: { woId: string; data: IUpdateWORequest }) =>
      mesService.updateWorkOrder(woId, data),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.wos(result.mo_id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.wo(result.id));
      },
    }
  );
};

export const useStartWorkOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, operatorId }: { woId: string; operatorId: string }) =>
      mesService.startWorkOrder(woId, operatorId),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.wos(result.mo_id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.wo(result.id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.mo(result.mo_id));
      },
    }
  );
};

export const useCompleteWorkOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, completedQty, scrappedQty }: { woId: string; completedQty: number; scrappedQty: number }) =>
      mesService.completeWorkOrder(woId, completedQty, scrappedQty),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.wos(result.mo_id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.wo(result.id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.mo(result.mo_id));
      },
    }
  );
};

export const useAssignOperator = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, data }: { woId: string; data: IAssignOperatorRequest }) =>
      mesService.assignOperator(woId, data),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.wos(result.mo_id));
        queryClient.invalidateQueries(MES_QUERY_KEYS.wo(result.id));
      },
    }
  );
};

// ============================================================================
// Defect Hooks
// ============================================================================

export const useDefects = (woId: string) => {
  return useQuery<IDefect[], Error>(
    MES_QUERY_KEYS.defects(woId),
    () => mesService.listDefects(woId),
    {
      enabled: !!woId,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreateDefect = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, data }: { woId: string; data: ICreateDefectRequest }) =>
      mesService.createDefect(woId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.defects(variables.woId));
      },
    }
  );
};

export const useUpdateDefect = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ defectId, data }: { defectId: string; woId: string; data: IUpdateDefectRequest }) =>
      mesService.updateDefect(defectId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.defects(variables.woId));
      },
    }
  );
};

// ============================================================================
// Quality Checkpoint Hooks
// ============================================================================

export const useCheckpoints = (woId: string) => {
  return useQuery<IQualityCheckpoint[], Error>(
    MES_QUERY_KEYS.checkpoints(woId),
    () => mesService.listCheckpoints(woId),
    {
      enabled: !!woId,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreateCheckpoint = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, data }: { woId: string; data: ICreateCheckpointRequest }) =>
      mesService.createCheckpoint(woId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.checkpoints(variables.woId));
      },
    }
  );
};

// ============================================================================
// Material Consumption Hooks
// ============================================================================

export const useMaterials = (woId: string) => {
  return useQuery<IMaterialConsumption[], Error>(
    MES_QUERY_KEYS.materials(woId),
    () => mesService.listMaterials(woId),
    {
      enabled: !!woId,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useRecordMaterial = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ woId, data }: { woId: string; data: IRecordMaterialRequest }) =>
      mesService.recordMaterial(woId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(MES_QUERY_KEYS.materials(variables.woId));
      },
    }
  );
};
